<template>
  <div class="blog-post-container">
    <div v-if="loading" class="loading">Loading post...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else-if="post" class="blog-post">
      <h2>{{ post.title }}</h2>
      <p class="post-meta">
        <span>{{ formatDate(post.publish_date || post.created_at) }}</span>
        <span v-if="post.views" class="views">• {{ post.views }} views</span>
      </p>
      <div v-if="post.tags && post.tags.length > 0" class="tags">
        <span v-for="tag in post.tags" :key="tag" class="tag">{{ tag }}</span>
      </div>
      <div v-html="post.content_html" class="post-content"></div>
    </div>
    <div v-else class="not-found">Post not found</div>

    <!-- Related Posts Section -->
    <div v-if="relatedPosts && relatedPosts.length > 0" class="related-posts">
      <h3>Related Posts</h3>
      <div class="related-posts-grid">
        <router-link
          v-for="related in relatedPosts"
          :key="related.slug"
          :to="`/blog/${related.slug}`"
          class="related-post-card"
        >
          <h4>{{ related.title }}</h4>
          <p class="post-excerpt">{{ related.excerpt }}</p>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { useBlog } from '@/services/blog';

export default {
  name: 'BlogPost',
  data() {
    return {
      post: null,
      relatedPosts: [],
      loading: true,
      error: null
    };
  },
  methods: {
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    async fetchPost() {
      const slug = this.$route.params.id;
      try {
        this.loading = true;
        const { getPublicPost, getRelatedPosts } = useBlog();

        // Fetch main post
        this.post = await getPublicPost(slug);

        // Fetch related posts (non-blocking, gracefully handle errors)
        try {
          const related = await getRelatedPosts(slug, 3);
          this.relatedPosts = related.posts || [];
        } catch (err) {
          console.warn('Related posts not available:', err);
          this.relatedPosts = [];
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        this.error = 'Failed to load blog post.';
      } finally {
        this.loading = false;
      }
    }
  },
  async created() {
    await this.fetchPost();
  },
  watch: {
    '$route.params.id': {
      handler: 'fetchPost',
      immediate: false
    }
  }
};
</script>

<style scoped>
.blog-post {
  font-family: 'Montserrat', sans-serif; /* Use Google Fonts */
  max-width: 800px;
  margin: 2rem auto;
  padding: 1rem;
  border-radius: 5px;
  background: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.blog-post h2 {
  margin-bottom: 1rem;
  text-align: center;
}

.blog-post p {
  margin-bottom: 1.5rem;
}

.inline-image {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 1rem 0;
}

.loading, .error, .not-found {
  text-align: center;
  padding: 3rem;
  color: #666;
}

.error, .not-found {
  color: #dc2626;
}

.post-meta {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.views {
  margin-left: 0.5rem;
}

.tags {
  text-align: center;
  margin-bottom: 2rem;
}

.tag {
  display: inline-block;
  background: #f0f0f0;
  padding: 0.25rem 0.75rem;
  margin: 0.25rem;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #555;
}

.post-content {
  line-height: 1.8;
  color: #333;
}

/* Style for markdown content */
.post-content h1,
.post-content h2,
.post-content h3 {
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.post-content p {
  margin-bottom: 1rem;
}

.post-content pre {
  background: #f4f4f4;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
}

.post-content code {
  background: #f4f4f4;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
}

.post-content pre code {
  background: none;
  padding: 0;
}

.post-content blockquote {
  border-left: 4px solid #007BFF;
  padding-left: 1rem;
  margin-left: 0;
  color: #666;
  font-style: italic;
}

.post-content img {
  max-width: 100%;
  height: auto;
  margin: 1rem 0;
}

.post-content ul,
.post-content ol {
  margin-bottom: 1rem;
  padding-left: 2rem;
}

.post-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.post-content th,
.post-content td {
  border: 1px solid #ddd;
  padding: 0.5rem;
  text-align: left;
}

.post-content th {
  background: #f4f4f4;
  font-weight: bold;
}

.related-posts {
  max-width: 800px;
  margin: 3rem auto 2rem;
  padding: 2rem 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.related-posts h3 {
  margin: 0 0 1.5rem 0;
  color: #2c3e50;
  text-align: center;
  font-size: 1.5rem;
}

.related-posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.related-post-card {
  background: white;
  padding: 1.5rem;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  display: block;
}

.related-post-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  text-decoration: none;
}

.related-post-card h4 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.post-excerpt {
  font-size: 0.9rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .related-posts-grid {
    grid-template-columns: 1fr;
  }
}
</style>